import * as Sentry from '@sentry/react'
import { clientesBack, adminUsuarioApi, organizationApi, registroUsuarioApi, companyApi } from '@/common/api/https'

/**
 * Un request cancelado con AbortController (por ejemplo, al desmontar la pantalla)
 * llega con status 0, como un error de red. No es un error: no se reporta a Sentry.
 * @param {unknown} e - Error de axios
 * @returns {boolean}
 */
export const isRequestCanceled = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError'

/**
 * Obtiene la lista de cargos disponibles
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getCargos = async () => {
  try {
    const res = await organizationApi.get('/parametros/cargo')
    return {
      code: res.status,
      data: res.data?.data || res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ORGANIZATION')
        scope.setTag('endpoint', '/parametros/cargo')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error desconocido',
    }
  }
}

/**
 * Obtiene la lista de Áreas de afinidad disponibles
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getAreasAfinidad = async () => {
  try {
    const res = await organizationApi.get('/parametros/area')
    return {
      code: res.status,
      data: res.data?.data || res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ORGANIZATION')
        scope.setTag('endpoint', '/parametros/area')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error desconocido',
    }
  }
}

/**
 * Consulta la empresa en Core Company para validar si la CUIT es cliente
 * de Provincia ART y tiene un contrato asignado.
 * Endpoint: GET {VITE_API_COMPANY_URL}/datos?cuit=XXXXXXXXXXX (CUIT sin guiones)
 * @param {string} cuit - CUIT sin guiones (solo dígitos)
 * @returns {Promise<{valida: boolean, motivo?: 'NO_EXISTE'|'SIN_CONTRATO'|'ERROR', error?: string, empresa?: {razonSocial: string, contrato: number|string|null}}>}
 */
export const getEmpresaPorCuit = async (cuit) => {
  try {
    const res = await companyApi.get('/datos', { params: { cuit } })

    const tieneContrato = res.data?.CONTRATO != null

    // Datos de la empresa para mostrar en la grilla de "Mis cuentas"
    const empresa = {
      razonSocial: res.data?.NOMBREEMPRESA ?? '',
      contrato: res.data?.CONTRATO ?? null,
    }

    return tieneContrato
      ? { valida: true, empresa }
      : { valida: false, motivo: 'SIN_CONTRATO', error: 'La CUIT no tiene un contrato asignado', empresa }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'COMPANY')
        scope.setTag('endpoint', '/datos')
        scope.setExtras({ method: e?.config?.method, cuit })
        Sentry.captureException(e)
      })
    }
    return {
      valida: false,
      motivo: status === 404 ? 'NO_EXISTE' : 'ERROR',
      error: e?.response?.data?.message || 'Error al validar la CUIT',
    }
  }
}

/**
 * Obtiene los contratos y solicitudes pendientes del usuario
 * @param {number} idUsuario - ID del usuario
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Señal para cancelar el request
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getContratosUsuario = async (idUsuario, { signal } = {}) => {
  try {
    const res = await adminUsuarioApi.get('/usuarios/contratos', {
      params: { idUsuario },
      signal,
    })
    return {
      code: res.status,
      data: res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ADMIN_USUARIO')
        scope.setTag('endpoint', '/usuarios/contratos')
        scope.setExtras({ idUsuario, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error al obtener contratos',
    }
  }
}

/**
 * Obtiene las empresas (cuentas) asociadas al usuario con el estado de cada una,
 * incluyendo el perfil médico por cuenta: "Si" | "No" | "DDJJ pendiente" |
 * "DDJJ pendiente de aprobación".
 * @param {number} idUsuario - ID del usuario
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Señal para cancelar el request
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getEmpresasUsuario = async (idUsuario, { signal } = {}) => {
  try {
    const res = await clientesBack.get(`/v2/usuarios/${idUsuario}/empresas`, { signal })
    return {
      code: res.status,
      data: res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/{idUsuario}/empresas')
        scope.setExtras({ idUsuario, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error al obtener las empresas del usuario',
    }
  }
}

/**
 * Obtiene los datos personales del usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise<{code: number, data: object|null, headers?: object}>}
 */
export const getDatosPersonales = async (idUsuario) => {
  try {
    const res = await clientesBack.get('/v2/usuarios/datos-personales', {
      params: { idUsuario },
    })
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/datos-personales')
        scope.setExtras({ idUsuario, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al obtener datos personales',
    }
  }
}

/**
 * Obtiene los atributos administrativos del usuario
 * @param {number} idUsuario - ID del usuario
 * @param {number} idSistemaWeb - ID del sistema web (default: 1)
 * @param {number} [contrato] - Contrato seleccionado (opcional)
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getAtributosUsuario = async (idUsuario, idSistemaWeb = 1, contrato = null, { signal } = {}) => {
  try {
    const params = { idUsuario, idSistemaWeb }
    if (contrato) {
      params.contrato = contrato
    }
    const res = await clientesBack.get('/v2/usuarios/atributos', {
      params,
      signal,
    })
    return {
      code: res.status,
      data: res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/atributos')
        scope.setExtras({ idUsuario, idSistemaWeb, contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error al obtener atributos del usuario',
    }
  }
}

/**
 * PBI 81906: estado de la DDJJ del médico en el contrato. El usuario lo resuelve
 * clientes_service desde la sesión. ddjjPendiente = médico y DDJJ no subida.
 * @param {number} contrato - Contrato activo
 * @returns {Promise<{code: number, data: {contrato, esMedico, ddjjSubida, ddjjAprobada, ddjjPendiente} | null, headers?: object, message?: string}>}
 */
export const getDdjjMedico = async (contrato) => {
  try {
    const res = await clientesBack.get('/v2/usuarios/ddjj-medico', {
      params: { contrato },
    })
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/ddjj-medico')
        scope.setExtras({ contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al obtener el estado de la DDJJ',
    }
  }
}

/**
 * Actualiza los datos personales del usuario
 * @param {Object} datosPersonales - Datos personales a actualizar
 * @param {number} datosPersonales.idUsuario - ID del usuario
 * @param {string} datosPersonales.apellido - Apellido del usuario
 * @param {string} datosPersonales.nombre - Nombre del usuario
 * @param {number} datosPersonales.tipoTelefono - Tipo de teléfono principal (ID)
 * @param {string} datosPersonales.telefono - Número de teléfono principal
 * @param {number} [datosPersonales.tipoTelefonoAlt] - Tipo de teléfono alternativo (ID)
 * @param {string} [datosPersonales.telefonoAlt] - Número de teléfono alternativo
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const updateDatosPersonales = async (datosPersonales) => {
  try {
    const res = await clientesBack.post('/v2/usuarios/datos-personales', datosPersonales)
    return {
      code: res.status,
      data: res.data || null,
      message: 'Datos personales actualizados correctamente',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/datos-personales')
        scope.setExtras({ datosPersonales, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al actualizar datos personales',
    }
  }
}

/**
 * Registra una solicitud de asociación de nueva cuenta para el usuario
 * @param {Object} payload - Datos de la solicitud
 * @param {string} payload.email - Email del usuario
 * @param {string} payload.cuit - CUIT sin guiones
 * @param {string} payload.apellido - Apellido del usuario
 * @param {string} payload.nombre - Nombre del usuario
 * @param {string} payload.idCargo - ID del cargo seleccionado
 * @param {string} payload.idAreaAfinidad - ID del área de afinidad seleccionada
 * @param {string|number} payload.tipoTelefono - ID del tipo de teléfono
 * @param {string} payload.numeroTelefono - Número de teléfono
 * @param {string} payload.aceptoTerminos - 'S' o 'N'
 * @param {string} payload.usuarioAlta - Email del usuario que da de alta
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const asociarCuenta = async (payload) => {
  try {
    const res = await registroUsuarioApi.post('/solicitudes', payload, {
      params: { validaMail: false },
    })
    return {
      code: res.status,
      data: res.data || null,
      message: 'Solicitud de asociación enviada correctamente',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'REGISTRO_USUARIO')
        scope.setTag('endpoint', '/solicitudes')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al enviar la solicitud de asociación',
    }
  }
}

/**
 * Da de baja la relación del usuario con un contrato (contrato en estado Activo), PBI 84320.
 * No da de baja al usuario: sólo desasocia la cuenta.
 * @param {Object} params
 * @param {number} params.idUsuario - ID del usuario dueño de la relación
 * @param {number} params.contrato - Contrato a desasociar
 * @param {string} params.usuAlta - Email de quien ejecuta la baja (auditoría)
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const bajaRelacionContrato = async ({ idUsuario, contrato, usuAlta }) => {
  try {
    const res = await adminUsuarioApi.delete('/usuarios/contratos', {
      params: { idUsuario, contrato, usuAlta },
    })
    return {
      code: res.status,
      data: res.data || null,
      message: res.data?.message || 'Relación con la cuenta dada de baja',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ADMIN_USUARIO')
        scope.setTag('endpoint', '/usuarios/contratos')
        scope.setExtras({ idUsuario, contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al dar de baja la relación con la cuenta',
    }
  }
}

/**
 * Rechaza una solicitud de asociación pendiente del propio usuario (solicitud en estado Pendiente).
 * El servicio valida que quien resuelve sea el dueño de la solicitud.
 * @param {Object} params
 * @param {number} params.idSolicitud - ID de la solicitud a rechazar
 * @param {number} params.idUsu - ID del usuario que rechaza (dueño de la solicitud)
 * @param {number} params.idRechazo - Motivo de rechazo del catálogo (16154 = "Otros")
 * @param {string} params.motRechazo - Detalle del motivo de rechazo
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const rechazarSolicitud = async ({ idSolicitud, idUsu, idRechazo, motRechazo }) => {
  try {
    const res = await adminUsuarioApi.post('/solicitudes', {
      idSolicitud,
      estado: 'R',
      fechaResolucion: new Date().toISOString(),
      idUsu,
      idRechazo,
      motRechazo,
    })
    return {
      code: res.status,
      data: res.data || null,
      message: res.data?.message || 'Solicitud rechazada',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ADMIN_USUARIO')
        scope.setTag('endpoint', '/solicitudes')
        scope.setExtras({ idSolicitud, idUsu, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al rechazar la solicitud',
    }
  }
}

/**
 * PBI 81147: sube la DDJJ del médico. El usuario lo resuelve clientes_service desde la sesión.
 * @param {{ contrato: number, archivo: File, signal?: AbortSignal }} params
 * @returns {Promise<{code: number, data: {contrato, ddjjSubida, ddjjPendiente, fechaCarga} | null, headers?: object, message?: string}>}
 */
export const subirDdjjMedico = async ({ contrato, archivo, signal }) => {
  try {
    const formData = new FormData()
    formData.append('contrato', String(contrato))
    formData.append('archivo', archivo)

    // Sin Content-Type explícito: axios arma multipart/form-data con el boundary.
    const res = await clientesBack.post('/v2/usuarios/ddjj-medico', formData, { signal })
    return { code: res.status, data: res.data || null, headers: res.headers }
  } catch (e) {
    if (e?.code === 'ERR_CANCELED') return { code: 0, data: null, cancelado: true }

    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/ddjj-medico')
        scope.setExtras({ contrato, method: e?.config?.method, size: archivo?.size, type: archivo?.type })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al cargar la Declaración Jurada',
    }
  }
}

/**
 * PBI 81147: template de la DDJJ con los datos del médico y del contrato, generado por
 * reporting desde clientes_service. El usuario lo resuelve el servicio desde la sesión.
 * Con responseType blob el cuerpo de un error también llega como Blob: el mensaje lo pone
 * el modal, no se lee del servicio.
 * @param {{ contrato: number }} params
 * @returns {Promise<{code: number, data: Blob | null}>}
 */
export const getTemplateDdjjMedico = async ({ contrato }) => {
  try {
    const res = await clientesBack.get('/v2/usuarios/ddjj-medico/template', {
      params: { contrato },
      responseType: 'blob',
    })
    return { code: res.status, data: res.data || null }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = !isRequestCanceled(e) && (status === 0 || status >= 500)
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/ddjj-medico/template')
        scope.setExtras({ contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return { code: status || 500, data: null }
  }
}
